import { Box } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useViews } from "../../../hooks/useView";
import {
  flyoutInputSx,
  chipSx,
  chipRemoveBtnSx,
  neutralChipRemoveBtnSx,
} from "./crudStyles";

interface ViewPickerInputProps {
  customerId: string;
  selectedViewIds: string[];
  onChange: (next: string[]) => void;
  candidateViewIds?: string[];
  chipVariant?: "add" | "remove" | "neutral";
}

export default function ViewPickerInput({
  customerId,
  selectedViewIds,
  onChange,
  candidateViewIds,
  chipVariant = "neutral",
}: ViewPickerInputProps) {
  const { t } = useTranslation(["common"]);
  const { data: views, isPending } = useViews(customerId);
  const [input, setInput] = useState("");
  const [focused, setFocused] = useState(false);

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const v of views ?? []) m.set(v.viewId, v.name);
    return m;
  }, [views]);

  const candidates = useMemo(() => {
    const all = views ?? [];
    const restrictedIds = candidateViewIds ? new Set(candidateViewIds) : null;
    const selected = new Set(selectedViewIds);
    return all
      .filter((v) => (restrictedIds ? restrictedIds.has(v.viewId) : true))
      .filter((v) => !selected.has(v.viewId))
      .filter((v) => (input.trim() ? v.name.toLowerCase().includes(input.toLowerCase()) : true))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [views, candidateViewIds, selectedViewIds, input]);

  const isLoading = isPending;
  const baseCount = (views ?? []).filter((v) =>
    candidateViewIds ? candidateViewIds.includes(v.viewId) : true,
  ).length;
  const hasNoViews = !isLoading && baseCount === 0;

  const placeholder = isLoading
    ? t("common:loadingViews")
    : hasNoViews
      ? t("common:noViewsAvailable")
      : t("common:selectOrTypeView");

  const removeBtnSx = chipVariant === "neutral" ? neutralChipRemoveBtnSx : chipRemoveBtnSx;

  const addView = (viewId: string) => {
    if (selectedViewIds.includes(viewId)) return;
    onChange([...selectedViewIds, viewId]);
    setInput("");
  };

  const removeView = (viewId: string) => {
    onChange(selectedViewIds.filter((v) => v !== viewId));
  };

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Box
          component="input"
          type="text"
          placeholder={placeholder}
          value={input}
          disabled={isLoading || hasNoViews}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          sx={flyoutInputSx}
        />
        {focused && candidates.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              zIndex: 10,
              mt: "4px",
              borderRadius: "8px",
              border: "1px solid var(--border-mid)",
              backgroundColor: "var(--bg-raised)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              maxHeight: 180,
              overflowY: "auto",
            }}
          >
            {candidates.map((v) => (
              <Box
                key={v.viewId}
                component="button"
                type="button"
                onClick={() => addView(v.viewId)}
                sx={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  px: "12px",
                  py: "8px",
                  fontSize: "0.8125rem",
                  fontFamily: "inherit",
                  color: "var(--text-primary)",
                  bgcolor: "transparent",
                  border: "none",
                  cursor: "pointer",
                  "&:hover": { bgcolor: "var(--bg-hover)" },
                }}
              >
                {v.name}
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {selectedViewIds.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mt: "8px" }}>
          {selectedViewIds.map((id) => {
            const name = nameById.get(id) ?? id;
            const sx = chipVariant === "neutral"
              ? {
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "4px 10px",
                  borderRadius: "999px",
                  backgroundColor: "var(--bg-hover)",
                  fontSize: "0.75rem",
                  color: "var(--text-secondary)",
                }
              : chipSx(chipVariant);
            return (
              <Box key={id} sx={sx}>
                {name}
                <Box
                  component="button"
                  type="button"
                  aria-label={`Remove ${name}`}
                  onClick={() => removeView(id)}
                  sx={removeBtnSx}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} style={{ width: 10, height: 10 }}>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
}
